/**
 * Status effects, split by where they came from.
 *
 * `StatusEffectSource` is the split: anything but `manual` was derived from a vital crossing a
 * threshold and leaves on its own when the vital comes back, while a manual effect — infection,
 * poison, radiation — stays until something removes it. That is a difference in what the player
 * should *do*, so it is a heading rather than a badge: drinking fixes the first list, and only
 * the second list is a problem you have to go and solve.
 *
 * It is also why the condition line above drops the derived half entirely (`condition.ts`):
 * `high_thirst` and the `dehydrated` thirst band are one fact, and the bar already showed it.
 *
 * `mechanical_effects` is `dict[str, float]` on the wire; `effects.ts` does the formatting,
 * and needs the translator because the modifier labels are bundle keys (L9).
 */
import type { StatusEffect } from '@/api/generated';
import { formatMechanicalEffect } from '@/features/character/effects';
import { useT } from '@/i18n/context';
import { Nothing } from '@/ui/Section';

export function StatusEffects({ effects }: { effects: readonly StatusEffect[] }) {
  const t = useT();

  if (effects.length === 0) return <Nothing>{t('ui.panel.nothing_on_you')}</Nothing>;

  const manual = effects.filter((effect) => effect.source === 'manual');
  const derived = effects.filter((effect) => effect.source !== 'manual');

  return (
    <div className="flex flex-col gap-4">
      {manual.length > 0 ? <Group effects={manual} label={t('ui.panel.until_treated')} /> : null}
      {derived.length > 0 ? (
        <Group effects={derived} label={t('ui.panel.while_numbers_hold')} />
      ) : null}
    </div>
  );
}

function Group({ effects, label }: { effects: readonly StatusEffect[]; label: string }) {
  const t = useT();

  return (
    <section>
      <h4
        className="text-fg-faint mb-2 font-mono tracking-wider"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        {label}
      </h4>
      <ul className="flex flex-col gap-3">
        {effects.map((effect) => (
          <li key={effect.type}>
            <p className="text-fg font-mono" style={{ fontSize: 'var(--text-meta)' }}>
              {effect.display_name}
            </p>
            <p className="text-fg-muted mt-1" style={{ fontSize: 'var(--text-meta)' }}>
              {effect.description}
            </p>
            {effect.mechanical_effects ? (
              <ul
                className="text-fg-faint mt-1 flex flex-wrap gap-x-3 font-mono"
                style={{ fontSize: 'var(--text-meta)' }}
              >
                {Object.entries(effect.mechanical_effects).map(([key, value]) => (
                  <li key={key}>{formatMechanicalEffect(t, key, value)}</li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
